/**
 * The postMessage protocol with the script the API embeds in the draft preview. The frame renders the customer's
 * own markup and may run their script, so everything arriving from it is untrusted until one of these guards says
 * otherwise.
 */

/** Posted by the embedded script when a page link is clicked. */
export const NAVIGATE_MESSAGE_TYPE = 'lev-draft-preview-navigate';
/** Posted by the embedded script as the reader scrolls, with `y`. */
export const SCROLLED_MESSAGE_TYPE = 'lev-draft-preview-scrolled';
/** Posted into a freshly loaded frame to put it back where the reader was. */
export const SCROLL_TO_MESSAGE_TYPE = 'lev-draft-preview-scroll-to';
/** Posted into the frame to turn element picking on and off. */
export const SELECT_MODE_MESSAGE_TYPE = 'lev-draft-preview-select-mode';
/** Posted by the embedded script when a picked element is clicked. */
export const ELEMENT_SELECTED_MESSAGE_TYPE = 'lev-draft-preview-element-selected';
/** Posted by the embedded script while a selection is open and the element moves under scroll or resize. */
export const ELEMENT_MOVED_MESSAGE_TYPE = 'lev-draft-preview-element-moved';
/** Posted by the embedded script when the selected element leaves the document. */
export const ELEMENT_GONE_MESSAGE_TYPE = 'lev-draft-preview-element-gone';
/** Posted by the embedded script when a click in select mode lands on markup with no element to address. */
export const ELEMENT_UNAVAILABLE_MESSAGE_TYPE = 'lev-draft-preview-element-unavailable';
/** Posted into the frame when the selection is dismissed, so the highlight comes off. */
export const CLEAR_SELECTION_MESSAGE_TYPE = 'lev-draft-preview-clear-selection';

interface PreviewRect {
	top: number;
	left: number;
	width: number;
	height: number;
}

/** Everything the embedded script can post out. Every field optional — nothing here is trusted until checked. */
interface PreviewFrameMessage {
	type?: string;
	path?: string;
	y?: number;
	elementIndex?: number;
	label?: string;
	rect?: PreviewRect;
	fieldPath?: string;
	fieldLabel?: string;
}

export interface PreviewElementSelection {
	/** Zero-based index into the page's element list — the only address the editor agent accepts. */
	elementIndex: number;
	label: string;
	rect: PreviewRect;
	/** Set when the click landed on one field of a multi-field element, e.g. `text` or `items[0].answer`. */
	fieldPath?: string;
	fieldLabel?: string;
}

/** Anything the frame posts arrives as unknown; this is the only place it is given a shape. */
export function asFrameMessage(data: unknown): PreviewFrameMessage | null {
	return data && typeof data === 'object' ? (data as PreviewFrameMessage) : null;
}

/** The accessor grammar the text write takes. This value reaches the model, so the shape is enforced here. */
const FIELD_PATH_PATTERN = /^[A-Za-z0-9_]+(\[\d+\])?(\.[A-Za-z0-9_]+(\[\d+\])?)*$/;
const FIELD_PATH_MAX_LENGTH = 128;

export function isFieldPath(value: unknown): value is string {
	return typeof value === 'string' && value.length <= FIELD_PATH_MAX_LENGTH && FIELD_PATH_PATTERN.test(value);
}

export function isElementIndex(value: unknown): value is number {
	return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

export function isRect(value: unknown): value is PreviewRect {
	if (!value || typeof value !== 'object') {
		return false;
	}
	const rect = value as Record<string, unknown>;
	return (['top', 'left', 'width', 'height'] as const).every(k => Number.isFinite(rect[k]));
}

export function isSelectionMessage(
	data: PreviewFrameMessage
): data is PreviewFrameMessage & { elementIndex: number; label: string; rect: PreviewRect } {
	return isElementIndex(data.elementIndex) && typeof data.label === 'string' && isRect(data.rect);
}
